import * as Sentry from "@sentry/browser";

import { SENTRY_APPLICATION_KEY } from "@/javascript/lib/sentry_application_key";

const ERRORS_TO_IGNORE: ErrorToIgnoreCallback[] = [
  // Sometimes jquery-ujs is loaded twice, which causes an error. This will not affect the user experience,
  // so we can safely ignore it in Sentry.
  (error) => {
    return error.message.includes("jquery-ujs has already been loaded");
  },

  // These errors are caused by the user navigating away from the page before a fetch request has completed.
  // They do not affect the user experience, so we can safely ignore them in Sentry.
  (error) => {
    return (
      error.name === "TypeError" &&
      [
        "Failed to fetch", // This is the error message in Chrome.
        "NetworkError when attempting to fetch resource.", // This is the error message in Firefox.
        "Load failed", // This is the error message in Safari.
      ].includes(error.message)
    );
  },

  // These errors are caused by the user cancelling a pending request, e.g. by clicking another pagination link
  // while a Turbo visit is still loading. We match on the name since the message differs between browsers.
  (error) => {
    return error.name === "AbortError";
  },

  // These errors are thrown inside Intercom's own scripts, e.g. when the user navigates away while the messenger is
  // loading or unloading. The messages vary between browsers and Intercom versions, so we match on the Intercom URL
  // of the frame the error was thrown in instead.
  (_error, event) => {
    return (
      !!_originatingFrameFilename(event)?.includes("js.intercomcdn.com") &&
      _hasThirdPartyCodeTag(event)
    );
  },

  // This Firefox-specific error is thrown by Bootstrap 3's tooltip plugin when a document-level
  // mousemove listener fires during Turbo navigation while the window context is being torn down.
  // We only ignore it when Sentry has confirmed it originates from third-party frames.
  (error, event) => {
    return (
      (error.name === "NS_ERROR_NOT_INITIALIZED" ||
        error.message.includes("NS_ERROR_NOT_INITIALIZED")) &&
      _hasThirdPartyCodeTag(event)
    );
  },

  // These errors are thrown inside Google's own Maps runtime. The messages vary between browsers and Maps
  // versions, so we match on the Maps URL instead. Only the frame the error was thrown in counts, since our own
  // Maps code can run inside Google's callbacks and then has Maps frames further down its stack.
  (error, event) => {
    const isFromGoogleMaps = [
      error.message,
      _originatingFrameFilename(event),
    ].some((text) =>
      ["maps.googleapis.com", "/maps-api-v3/"].some((mapsUrl) =>
        text?.includes(mapsUrl)
      )
    );

    return isFromGoogleMaps && _hasThirdPartyCodeTag(event);
  },
];

const sentryConfig = window.sentryConfig;

if (sentryConfig) {
  Sentry.init({
    dsn: sentryConfig.dsn,
    environment: sentryConfig.environment ?? undefined,
    // The default ("always") appends the hostname to error.message in place, which breaks the exact
    // message matching in ERRORS_TO_IGNORE. "report-only" still adds the hostname to the Sentry event.
    enhanceFetchErrorMessages: "report-only",
    // Older Chrome reports this harmless warning through window.onerror without an error object, so beforeSend
    // cannot see it. The SDK's default filter only covers the newer "loop completed with undelivered notifications".
    ignoreErrors: [/^ResizeObserver loop limit exceeded$/],
    integrations: [
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [SENTRY_APPLICATION_KEY],
        behaviour: "drop-error-if-exclusively-contains-third-party-frames",
      }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [SENTRY_APPLICATION_KEY],
        behaviour: "apply-tag-if-contains-third-party-frames",
      }),
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;

      if (
        _isErrorWithNameAndMessage(error) &&
        ERRORS_TO_IGNORE.some((shouldIgnore) => shouldIgnore(error, event))
      ) {
        return null;
      }

      return event;
    },
  });

  if (sentryConfig.userId !== null && sentryConfig.userId !== undefined) {
    Sentry.setUser({ id: sentryConfig.userId.toString() });
  }
}

type ErrorToIgnoreCallback = (
  error: ErrorWithNameAndMessage,
  event: Sentry.ErrorEvent
) => boolean;

type ErrorWithNameAndMessage = {
  name: string;
  message: string;
};

function _hasThirdPartyCodeTag(event: Sentry.ErrorEvent): boolean {
  return String(event.tags?.["third_party_code"]).toLowerCase() === "true";
}

// Sentry orders exceptions and stack frames oldest first, so the thrown error and its throwing frame come last.
function _originatingFrameFilename(
  event: Sentry.ErrorEvent
): string | undefined {
  const exceptions = event.exception?.values ?? [];
  const frames = exceptions[exceptions.length - 1]?.stacktrace?.frames ?? [];

  return frames[frames.length - 1]?.filename;
}

function _isErrorWithNameAndMessage(
  error: unknown
): error is ErrorWithNameAndMessage {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    "message" in error &&
    typeof error.name === "string" &&
    typeof error.message === "string"
  );
}
